import { useMutation } from '@apollo/client';
import type { InsertImpactMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertImpactDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertImpactTRPCInput } from './useInsertImpactTRPC';
import { useInsertImpactTRPC } from './useInsertImpactTRPC';

export const useInsertImpact = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertImpactGraphQL, graphqlState] = useMutation(
    InsertImpactDocument,
    {
      update: (cache) => {
        evictField(cache, 'impact');
        evictField(cache, 'impact_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertImpactTRPC();

  const insertImpact = async (
    variables: InsertImpactTRPCInput
  ): Promise<InsertImpactMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertImpact(variables);
    }

    const result = await insertImpactGraphQL({
      variables: {
        object: variables,
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert impact');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertImpact,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertImpact,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
